/**
 * Chart documentation shown behind the info icon on the Retention dashboard.
 *
 * Keys match the chart link property names in `MopaRetentionComponent`.
 */
export const MopaRetentionChartDocs = {
    retention_per_day_no: /* HTML */ `<p>
            Share of a cohort still active on a specific day-number since
            install (classic N-day retention).
        </p>
        <pre>
  players active on exactly that day-number         (numerator)
  ──────────────────────────────────────────
  players matured to at least that day-number       (denominator)</pre
        >
        <p>
            The maturity-based <strong>denominator</strong> is the key point:
            cohorts too young to have reached day N are <em>excluded</em> from
            both sides, not counted as churned, so recent installs don't drag
            the curve down. Day 0 is 100% by construction.
        </p>
        <p>Source: <code>bi_viz.dashboard_retention</code>.</p>`,

    rolling_retention_per_day_no: /* HTML */ `<p>
            Share of a cohort that has been active at least once on or after a
            given day-number - a more forgiving, "bring-back" variant of
            retention.
        </p>
        <pre>
  players active at least once on/after that day-number   (numerator)
  ─────────────────────────────────────────────────────
  players matured to at least that day-number             (denominator)</pre
        >
        <p>
            The <strong>numerator</strong> counts a player if their latest
            active day (capped at their maturity) is at or beyond that
            day-number - they returned at some point, not necessarily on that
            exact day. The <strong>denominator</strong> is the same
            maturity-based cohort as classic retention. Because "came back
            later" counts, rolling retention is always ≥ classic retention at
            the same day-number and decreases more slowly.
        </p>
        <p>Source: <code>bi_viz.dashboard_retention</code>.</p>`,

    retention_by_first_open_date: /* HTML */ `<p>
            N-day retention (D1, D7, D30, …) tracked by the cohort's first-open
            date rather than by day-number. Each selected day-number is its own
            series plotted across install dates.
        </p>
        <pre>
  cohort players active on day N                        (numerator)
  ────────────────────────────────────────────
  cohort's day-0 size (all who first opened that date)  (denominator)</pre
        >
        <p>
            Note the <strong>denominator</strong> differs from the by-day-no
            charts: here it is the <em>fixed</em> day-0 cohort, <em>not</em>
            narrowed by maturity. Cohorts too young to have reached day N have
            no data point yet and show a gap rather than a misleading 0%.
        </p>
        <p>Source: <code>bi_viz.dashboard_retention</code>.</p>`,

    rolling_retention_by_first_open_date: /* HTML */ `<p>
            Rolling ("bring-back") retention tracked by the cohort's first-open
            date rather than by day-number. Each selected day-number is its own
            series plotted across install dates.
        </p>
        <pre>
  cohort players active at least once on/after day N    (numerator)
  ────────────────────────────────────────────
  cohort's day-0 size (all who first opened that date)  (denominator)</pre
        >
        <p>
            As in the plain by-first-open-date chart, the
            <strong>denominator</strong> is the <em>fixed</em> day-0 cohort (not
            maturity-narrowed), and too-young cohorts show a gap rather than 0%.
        </p>
        <p>Source: <code>bi_viz.dashboard_retention</code>.</p>`,

    avg_user_lifetime: /* HTML */ `<p>
            Estimated average number of days a user stays active, based on the
            retention observed up to that day-number - the "area under the
            retention curve".
        </p>
        <p>
            <strong>How it's built:</strong> take the by-day-no retention rate,
            sum those rates from day 0 up to the current day-number, then
            subtract 1.
        </p>
        <p>
            The −1 drops day 0, which is 100% for everyone and would otherwise
            inflate the estimate by a full day. The value therefore grows as
            more retained days accumulate and approximates expected lifetime in
            days.
        </p>
        <p>Source: <code>bi_viz.dashboard_retention</code>.</p>`,
} as const;
